import React, { useState } from "react";
import "./RothEducationHelper.css";
import Header from "../Header/Header";


const RothEducationHelper = () => {
  const [ageGroup, setAgeGroup] = useState("under59"); // "under59" | "over59"
  const [withdrawType, setWithdrawType] = useState("contribution"); // "contribution" | "conversion" | "earnings"
  const [conversion5, setConversion5] = useState(false); // specific conversion ≥5 yrs?
  const [roth5, setRoth5] = useState(false); // any Roth in your name ≥5 yrs?
  const [education, setEducation] = useState(false); // using exception?

  const computeResult = () => {
    let tax = "No";
    let penalty = "No";
    const notes = [];

    // 1) CONTRIBUTIONS (your own Roth contributions)
    if (withdrawType === "contribution") {
      tax = "No";
      penalty = "No";
      notes.push("Contributions are always tax-free and penalty-free.");
      notes.push("Education, age, and 5-year rules do not change this.");
      return { tax, penalty, notes };
    }

    // 2) CONVERTED PRINCIPAL (fully taxable conversion from deductible IRA)
    if (withdrawType === "conversion") {
      // Tax: never again at withdrawal
      tax = "No";

      if (ageGroup === "over59") {
        penalty = "No";
        notes.push("You are 59½ or older, so there is no penalty on converted principal.");
      } else {
        // under 59½
        if (conversion5) {
          penalty = "No";
          notes.push("This conversion has been in the Roth for at least 5 tax years → no 10% penalty.");
        } else {
          // conversion <5yrs, under 59½
          if (education) {
            penalty = "No";
            notes.push(
              "Under 59½ and conversion <5 years, but the education exception removes the 10% penalty on this converted amount."
            );
          } else {
            penalty = "Yes";
            notes.push("Under 59½ and conversion <5 years → 10% penalty on the converted amount.");
          }
        }
      }

      notes.push("This assumes the original IRA was fully deductible and the whole conversion was taxable.");
      return { tax, penalty, notes };
    }

    // 3) EARNINGS (growth)
    if (withdrawType === "earnings") {
      if (ageGroup === "under59") {
        // Under 59½ → earnings always taxed
        tax = "Yes";

        if (education) {
          penalty = "No";
          notes.push(
            "Under 59½, earnings are taxable. The education exception removes the 10% penalty, but not the tax."
          );
        } else {
          penalty = "Yes";
          notes.push(
            "Under 59½ and no exception → earnings are taxed and subject to the 10% early withdrawal penalty."
          );
        }
      } else {
        // 59½ or older
        if (roth5) {
          tax = "No";
          penalty = "No";
          notes.push("Over 59½ and your Roth IRA has been open at least 5 tax years → earnings are tax-free and penalty-free.");
        } else {
          tax = "Yes";
          penalty = "No";
          notes.push("Over 59½ but Roth IRA is under 5 tax years → earnings are taxed, but no 10% penalty.");
        }
        notes.push("The education exception does not change the tax on earnings when you are 59½ or older.");
      }

      return { tax, penalty, notes };
    }

    return { tax, penalty, notes };
  };

  const { tax, penalty, notes } = computeResult();

  return (
        <div className="min-h-screen bg-gradient-to-b from-orange-100 via-orange-50 to-slate-50 text-slate-900">
       <Header/>
    <div className="roth-ed-helper">
      <h1 className="roth-ed-title">Roth IRA – Tax & Penalty Helper (with Education Exception)</h1>
      <p className="roth-ed-subtitle">
        Educational-only tool to show when <strong>income tax</strong> and the
        <strong> 10% early withdrawal penalty</strong> might apply.
      </p>

      <div className="roth-ed-card">
        {/* Age */}
        <div className="roth-ed-field">
          <label className="roth-ed-label">1. How old are you?</label>
          <div className="roth-ed-options">
            <label className="roth-ed-radio">
              <input
                type="radio"
                value="under59"
                checked={ageGroup === "under59"}
                onChange={(e) => setAgeGroup(e.target.value)}
              />
              Under 59½
            </label>
            <label className="roth-ed-radio">
              <input
                type="radio"
                value="over59"
                checked={ageGroup === "over59"}
                onChange={(e) => setAgeGroup(e.target.value)}
              />
              59½ or older
            </label>
          </div>
        </div>

        {/* What are you taking out? */}
        <div className="roth-ed-field">
          <label className="roth-ed-label">2. What are you taking out of the Roth IRA?</label>
          <select
            className="roth-ed-select"
            value={withdrawType}
            onChange={(e) => setWithdrawType(e.target.value)}
          >
            <option value="contribution">My own contributions (money I originally put in)</option>
            <option value="conversion">Converted money (from a deductible IRA/401k)</option>
            <option value="earnings">Earnings / growth</option>
          </select>
        </div>

        {/* Conversion 5-year question */}
        {withdrawType === "conversion" && (
          <div className="roth-ed-field">
            <label className="roth-ed-label">
              3. Has this specific conversion been in the Roth IRA for at least 5 tax years?
            </label>
            <span className="roth-ed-help">
              This is the separate 5-year clock for conversions. An old Roth opened in 2000 does
              not shorten this.
            </span>
            <div className="roth-ed-options">
              <label className="roth-ed-radio">
                <input
                  type="radio"
                  value="true"
                  checked={conversion5 === true}
                  onChange={() => setConversion5(true)}
                />
                Yes
              </label>
              <label className="roth-ed-radio">
                <input
                  type="radio"
                  value="false"
                  checked={conversion5 === false}
                  onChange={() => setConversion5(false)}
                />
                No
              </label>
            </div>
          </div>
        )}

        {/* Roth 5-year account question (earnings) */}
        {withdrawType === "earnings" && (
          <div className="roth-ed-field">
            <label className="roth-ed-label">
              3. Has any Roth IRA in your name been open for at least 5 tax years?
            </label>
            <span className="roth-ed-help">
              Example: A Roth first opened in 2000 always satisfies this 5-year rule.
            </span>
            <div className="roth-ed-options">
              <label className="roth-ed-radio">
                <input
                  type="radio"
                  value="true"
                  checked={roth5 === true}
                  onChange={() => setRoth5(true)}
                />
                Yes
              </label>
              <label className="roth-ed-radio">
                <input
                  type="radio"
                  value="false"
                  checked={roth5 === false}
                  onChange={() => setRoth5(false)}
                />
                No
              </label>
            </div>
          </div>
        )}

        {/* Education exception question (only relevant for conversion/earnings, but harmless to always show for those) */}
        {withdrawType !== "contribution" && (
          <div className="roth-ed-field">
            <label className="roth-ed-label">
              4. Is this withdrawal for <strong>qualified higher-education expenses</strong>?
            </label>
            <span className="roth-ed-help">
              The education exception can remove the 10% penalty on taxable amounts if you are under 59½, but it does not remove income tax.
            </span>
            <div className="roth-ed-options">
              <label className="roth-ed-radio">
                <input
                  type="radio"
                  value="false"
                  checked={education === false}
                  onChange={() => setEducation(false)}
                />
                No
              </label>
              <label className="roth-ed-radio">
                <input
                  type="radio"
                  value="true"
                  checked={education === true}
                  onChange={() => setEducation(true)}
                />
                Yes
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="roth-ed-result-card">
        <h2 className="roth-ed-result-title">Result (Educational Only)</h2>
        <div className="roth-ed-result-row">
          <span className="roth-ed-result-label">Income tax on this piece?</span>
          <span className={`roth-ed-pill ${tax === "Yes" ? "roth-ed-pill-bad" : "roth-ed-pill-good"}`}>
            {tax}
          </span>
        </div>
        <div className="roth-ed-result-row">
          <span className="roth-ed-result-label">10% early withdrawal penalty?</span>
          <span
            className={`roth-ed-pill ${penalty === "Yes" ? "roth-ed-pill-bad" : "roth-ed-pill-good"}`}
          >
            {penalty}
          </span>
        </div>

        {notes.length > 0 && (
          <ul className="roth-ed-notes">
            {notes.map((n, idx) => (
              <li key={idx}>{n}</li>
            ))}
          </ul>
        )}

        <p className="roth-ed-disclaimer">
          This tool is for learning only. It does not give tax, legal, or financial advice. Real
          outcomes depend on full IRS rules and your complete situation. Review IRS Pub. 590-B and
          talk to a qualified tax professional.
        </p>
      </div>
    </div> </div>
  );
};

export default RothEducationHelper;
